/**
 * EPG Guide Component
 * Cable TV-style program guide grid
 */

/**
 * Normalize a channel name for fuzzy matching between the Xtream live channel
 * list and the xmltv EPG channel list. Many providers (including the one this
 * app was built against) use the same "COUNTRY| CHANNEL NAME [QUALITY]"
 * convention in both, but not identically - e.g. Xtream "CA| CTV RED DEER" vs
 * xmltv "CA| CTV Red Deer HD". A large fraction of channels have no
 * epg_channel_id from the provider at all (verified: ~80% on this app's real
 * catalog), so exact-string name matching - which only succeeds for a tiny
 * fraction of those - was leaving most channels with no visible EPG data even
 * though the provider's xmltv feed actually has an entry for many of them.
 * Stripping the prefix/quality noise and collapsing whitespace/punctuation
 * bridges most of that gap.
 */
/**
 * Parse an epoch-ms value that may arrive as a real number (in-memory
 * programme objects) or as a string (anything read back off a clicked
 * element's `dataset` - DOMStringMap values are always strings, even though
 * `data-start="${prog.start}"` was written from a number). `new Date(x)`
 * is NOT interchangeable between the two: given a number it's a raw epoch
 * parse, but given a string it runs the date-string parser (ISO 8601 etc),
 * and a pure-digit string like "1785315960000" isn't a valid date string -
 * it silently produces Invalid Date / NaN instead of the intended
 * timestamp. This broke catch-up/timeshift playback (NaN start/duration
 * sent to the server), the programme-detail modal's time display, and its
 * description fetch, all of which read start/stop off a dataset.
 */
function parseEpochMs(value) {
    if (typeof value === 'string' && /^\d+$/.test(value)) return parseInt(value, 10);
    return new Date(value).getTime();
}

function normalizeChannelName(name) {
    if (!name) return '';
    return name
        .toUpperCase()
        .replace(/^[A-Z]{2,4}\s*\|\s*/, '') // strip "CA| ", "US| ", "4K| " country/category prefix
        .replace(/\b(HD|FHD|UHD|4K|SD|HEVC|H265|RAW)\b/g, '') // strip quality tokens
        .replace(/[^A-Z0-9]+/g, ''); // collapse remaining whitespace/punctuation
}

class EpgGuide {
    constructor() {
        this.container = document.getElementById('epg-grid');
        this.dateDisplay = document.getElementById('guide-date');
        this.prevBtn = document.getElementById('guide-prev');
        this.nextBtn = document.getElementById('guide-next');
        this.nextBtn = document.getElementById('guide-next');
        this.categoryRail = new CategoryRail(document.getElementById('epg-category-rail'), {
            onChange: (value) => this.selectCategory(value)
        });
        this.searchInput = document.getElementById('epg-search');

        this.channels = [];
        this.programmes = [];
        // Guards playCatchupProgram() - a slower getTimeshiftUrl() from an
        // earlier catch-up click (or a catch-up click racing a live-channel
        // click) must not invoke player.play() after a faster later
        // selection already did. Same pattern as ChannelList's own
        // channelSelectionGeneration.
        this.catchupSelectionGeneration = 0;
        // Guards toggleFavorite() below - same reasoning as MoviesPage.js's
        // _toggleInFlight: the optimistic update happens before the await, so
        // a double-click on the same channel's favorite button would
        // otherwise read the already-flipped state and fire the opposite API
        // call, racing with the first at the server.
        this._toggleInFlight = new Set();
        this.currentDate = new Date();
        this.timeOffset = 0; // Hours offset from now
        // How far back the rendered grid extends before "now" (+ timeOffset),
        // so already-aired programmes stay visible/scrollable for catch-up -
        // matches EPG_RETENTION_MS in syncService.js and the window
        // computeEpgProgrammes() queries server-side.
        this.pastHours = 72; // 3 days
        this.pixelsPerMinute = 6.67; // Width scaling (30min = 200px)
        this.favorites = new Set(); // Set<"sourceId:channelId">
        // Distinguishes "favorites data hasn't loaded yet" from "genuinely no
        // favorites" - fetchEpgData() populates this.favorites, but render()
        // can now run BEFORE that (see the channels-render-before-EPG change
        // in Guide.js's show()). Without this flag, that early render saw an
        // empty this.favorites, treated it as "user has no favorites", and
        // permanently fell back selectedGroup to 'All Groups' - which then
        // stuck even once real favorites loaded a moment later, since the
        // fallback only fires while selectedGroup is still 'Favorites'.
        this.favoritesLoaded = false;
        this.selectedGroup = 'Favorites'; // Default to Favorites

        // Virtual scrolling properties
        this.filteredChannels = [];
        this.rowHeight = 60; // Height of each channel row in pixels
        this.bufferRows = 5; // Extra rows to render above/below viewport
        this.startTime = null;
        this.endTime = null;
        this.epgContainer = null;
        this.epgSpacer = null;
        this.scrollContainer = null;
        this.visibleRows = new Map(); // Map<index, rowElement>
        this._scrollHandler = null;
        this._lastVisibleStart = -1;
        this._lastVisibleEnd = -1;

        // Map<sourceId, lastSync ms> from the last time we actually fetched
        // (or checked) each source's EPG data - see checkForEpgUpdates().
        this._epgSyncMeta = {};

        // Programmes are loaded incrementally/on-demand (favorites first, then
        // per-group as the user switches groups) rather than the whole catalog
        // up front - see loadEpg()/selectCategory(). These track what's been
        // loaded so we never refetch a channel's schedule or re-do the
        // expensive full-catalog pull.
        this._loadedProgrammeChannelIds = new Set(); // Set<epgChannelId>
        this._allProgrammesLoaded = false;           // true once the whole catalog is in

        this.init();
    }

    /**
     * Get a proxied, resized channel-logo URL. Always routed through our
     * own proxy now (not just for HTTP-on-HTTPS mixed-content avoidance) -
     * `/api/proxy/image` resizes+converts to WebP and caches the result
     * server-side, and logos are only ever shown at 32px (.epg-channel-logo)
     * so there's no reason to ship a full-resolution original.
     */
    getProxiedImageUrl(url) {
        if (!url || url.length === 0) return '/img/placeholder.png';
        return `/api/proxy/image?url=${encodeURIComponent(url)}&w=64`;
    }

    init() {
        this.prevBtn.addEventListener('click', () => this.navigate(-2));
        this.nextBtn.addEventListener('click', () => this.navigate(2));

        // Search input - lightweight refilter, not a full render() (see
        // refilterInPlace() for why: a full render() resets scroll position).
        if (this.searchInput) {
            this.searchInput.addEventListener('input', this.debounce(() => {
                this.refilterInPlace();
            }, 300));
        }

        // Current-time indicator interval is (re)created per render() call
        // (properly cleared/replaced there) - not started here to avoid a
        // permanent second, redundant 60s timer running alongside it.

        this.initResizer();
    }

    /**
     * Initialize sidebar resizer
     */
    initResizer() {
        let isResizing = false;
        let startX, startWidth;

        document.addEventListener('mousedown', (e) => {
            if (e.target.classList.contains('resize-handle')) {
                isResizing = true;
                startX = e.clientX;
                const sidebar = document.querySelector('.epg-channel-info');
                startWidth = sidebar.getBoundingClientRect().width;
                e.target.classList.add('active');
                document.body.style.cursor = 'col-resize';
                e.preventDefault(); // Prevent text selection
            }
        });

        document.addEventListener('mousemove', (e) => {
            if (!isResizing) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(150, Math.min(500, startWidth + diff)); // Min 150px, Max 500px

            document.documentElement.style.setProperty('--epg-sidebar-width', `${newWidth}px`);
            this.updateNowIndicator(); // Update line position
        });

        document.addEventListener('mouseup', () => {
            if (isResizing) {
                isResizing = false;
                document.querySelectorAll('.resize-handle').forEach(h => h.classList.remove('active'));
                document.body.style.cursor = '';
            }
        });
    }

    /**
     * Select a category chip (group name, '' for All Groups, or 'Favorites')
     */
    async selectCategory(groupName) {
        this.selectedGroup = groupName;

        // Ensure the schedule data this group needs is loaded before painting.
        // Favorites is already loaded on open; other groups load on demand so
        // the initial guide never has to pull the whole catalog. Idempotent -
        // _fetchProgrammesScoped skips already-loaded channels, so re-visiting
        // a group costs nothing.
        try {
            await this._ensureProgrammesForGroup(groupName);
        } catch (e) {
            console.warn('[EPG] Failed to load programmes for group:', groupName, e);
        }
        this.render();
    }

    /**
     * Load the programme schedules needed to display a given category, if not
     * already loaded. Favorites and specific groups fetch just their own
     * channels (scoped, fast); "All Groups" (''/'All') genuinely needs every
     * channel, so it does the one-time full-catalog pull. No-op once the data
     * is present.
     */
    async _ensureProgrammesForGroup(groupName) {
        if (this._allProgrammesLoaded) return;

        const liveChannels = window.app?.channelList?.channels || [];

        // All Groups -> needs everything. Pull the full catalog once.
        if (!groupName || groupName === 'All') {
            const before = this.container.querySelector('.epg-container');
            if (before) this.container.innerHTML = '<div class="loading"></div>';
            await this.fetchEpgData();
            return;
        }

        // Favorites -> loaded on open (or will be handled by loadEpg's scope).
        let targetChannels;
        if (groupName === 'Favorites') {
            targetChannels = liveChannels.filter(ch => this.favorites.has(`${ch.sourceId}:${ch.id}`));
        } else {
            targetChannels = liveChannels.filter(ch => (ch.groupTitle || 'Uncategorized') === groupName);
        }

        const scope = this._scopeForLiveChannels(targetChannels);
        if (scope) {
            await this._fetchProgrammesScoped(scope);
        }
    }

    /**
     * Navigate time
     */
    navigate(hours) {
        this.timeOffset += hours;
        this.render();
    }

    /**
     * Start background EPG freshness-check timer.
     *
     * The server only actually gets new EPG data when a source syncs (every
     * few hours, not on this timer's cadence), so re-downloading and
     * re-parsing the full multi-MB guide payload here would be pure waste
     * the overwhelming majority of the time. Instead this polls the tiny
     * /epg/:sourceId/meta endpoint (a single indexed timestamp lookup) and
     * only pulls the full payload if a source's last-synced time actually
     * moved - see checkForEpgUpdates().
     */
    startBackgroundRefresh() {
        // Clear any existing timer
        this.stopBackgroundRefresh();

        const refreshIntervalMs = 15 * 60 * 1000; // 15 minutes

        console.log('[EPG] Starting freshness-check timer: every 15 minutes');

        this._backgroundRefreshTimer = setInterval(async () => {
            try {
                await this.checkForEpgUpdates();
            } catch (err) {
                console.error('[EPG] Freshness check failed:', err);
            }
        }, refreshIntervalMs);
    }

    /**
     * Cheap freshness check, run periodically while the guide is open (see
     * startBackgroundRefresh). Compares each EPG-capable source's last-sync
     * timestamp against what we saw on our last real fetch; only triggers a
     * full fetchEpgData()+render() if something actually changed upstream.
     */
    async checkForEpgUpdates() {
        const allSources = await API.sources.getAll();
        const sources = allSources.filter(s => (s.type === 'epg' || s.type === 'xtream') && s.enabled);

        let changed = false;
        await Promise.all(sources.map(async (source) => {
            try {
                const res = await fetch(`/api/proxy/epg/${source.id}/meta`);
                if (!res.ok) return;
                const { lastSync } = await res.json();
                if (lastSync !== this._epgSyncMeta[source.id]) {
                    changed = true;
                }
            } catch (e) {
                // Network hiccup on the cheap check - just try again next tick,
                // not worth treating as "changed" and paying for a full fetch.
            }
        }));

        if (changed) {
            console.log('[EPG] Upstream sync detected, refreshing guide data');
            // Reload only what's actually loaded/visible, not the whole
            // catalog - if the user's been browsing Favorites we just re-pull
            // Favorites, keeping the refresh as cheap as the initial load.
            // (If they'd previously loaded All Groups, _ensureProgrammesFor
            // Group does a full refresh.) Refresh the small channel-identity
            // list first so favorites still resolve if the sync changed it.
            this.programmes = [];
            this._loadedProgrammeChannelIds = new Set();
            this._allProgrammesLoaded = false;
            const wasAllLoaded = this.selectedGroup === '' || this.selectedGroup === 'All';
            if (!wasAllLoaded) await this.loadEpgChannelsOnly();
            await this._ensureProgrammesForGroup(this.selectedGroup);
            this.lastRefreshTime = new Date();
            this.render();
        }
    }

    /**
     * Stop background EPG refresh timer
     */
    stopBackgroundRefresh() {
        if (this._backgroundRefreshTimer) {
            clearInterval(this._backgroundRefreshTimer);
            this._backgroundRefreshTimer = null;
            console.log('[EPG] Background refresh timer stopped');
        }
    }

    /**
     * Get last refresh time for display
     */
    getLastRefreshTime() {
        return this.lastRefreshTime || null;
    }

    /**
     * Load EPG data (server-side caching)
     *
     * De-duped: app.js's idle-time background preload and a direct
     * Guide.show() navigation (e.g. a hard reload on #guide) can both call
     * this within milliseconds of each other at boot. Without this guard
     * they'd each kick off a full parallel EPG fetch (6MB+ gzipped) -
     * wasted bandwidth on a connection that's often already the bottleneck,
     * and whichever one resolved first would call render() using whatever
     * channel data happened to be loaded at that moment, which could still
     * be empty (see the fix in Guide.js's show(), which now also guarantees
     * a final correct render after both channels and EPG are ready).
     */
    /**
     * Load EPG data.
     *
     * Phased for a fast first paint: (1) fetch just the small EPG channel
     * list for every source, so favorited live channels can be resolved to
     * their EPG identity; (2) if that resolves to a non-empty scope, fetch
     * *only* those channels' programmes (small, fast) and render - real
     * schedule data on screen almost immediately, instead of waiting on the
     * entire catalog; (3) fill in the rest of the catalog in the background
     * (via a Worker, so parsing tens of MB of JSON never blocks the UI) and
     * re-render once that lands. Falls back to a plain full fetch if there's
     * no favorites/live-channel data to scope against yet - still correct,
     * just not faster.
     *
     * @param {Array} [liveChannels] - channelList.channels, if already
     *   loaded (Guide.show() passes this after awaiting it). Falls back to
     *   window.app.channelList.channels so the idle-time boot preload (see
     *   app.js), which doesn't have this explicitly, still benefits if that
     *   list happens to already be loaded by then.
     */
    async loadEpg(liveChannels) {
        if (this._loadPromise) return this._loadPromise;
        this._loadPromise = this._doLoadEpg(liveChannels);
        try {
            await this._loadPromise;
        } finally {
            this._loadPromise = null;
        }
    }

    async _doLoadEpg(liveChannels) {
        try {
            this.container.innerHTML = '<div class="loading"></div>';

            // Fresh load - reset the incremental programme state so this
            // load's scoped fetches start clean.
            this.programmes = [];
            this._loadedProgrammeChannelIds = new Set();
            this._allProgrammesLoaded = false;

            await Promise.all([
                this.loadEpgChannelsOnly(),
                this.loadFavorites().catch(() => { }),
            ]);

            const channels = liveChannels || window.app?.channelList?.channels || null;
            const scope = this._resolveScopedChannelIdsBySource(channels);

            if (scope) {
                // Load only the favorited channels' schedules and paint. The
                // rest of the catalog is NOT eagerly loaded - switching to a
                // non-Favorites group fetches just that group on demand (see
                // selectCategory). Eagerly pulling all ~300k programmes here
                // was invisible on a fast pipe but blocked the main thread
                // for seconds parsing/receiving them on real mobile, right
                // after the guide had already appeared - the "loads then goes
                // janky/frozen" symptom.
                await this._fetchProgrammesScoped(scope);
                this.lastRefreshTime = new Date();
                this.render();
            } else {
                // No favorites to scope against (or channels not loaded yet) -
                // the default view will be All Groups, which needs everything.
                await this.fetchEpgData();
                this.lastRefreshTime = new Date();
                this.render();
            }

            // Start background freshness-check timer after initial load
            // This ensures EPG data stays fresh while the app is open
            this.startBackgroundRefresh();
        } catch (err) {
            console.error('Error loading EPG:', err);
            this.container.innerHTML = `
        <div class="empty-state">
          <p>Error loading EPG</p>
          <p class="hint">${err.message}</p>
        </div>
      `;
        }
    }

    /**
     * Fetch just the (small) EPG channel list for every source - enough to
     * resolve live-channel favorites to their EPG identity, without paying
     * for the much heavier programmes payload. Populates this.channels,
     * this._channelsBySource (needed to know which source to ask for a
     * given resolved channel's programmes) and the channelMap/
     * channelMapNormalized lookups used by resolveEpgChannel().
     */
    async loadEpgChannelsOnly() {
        const allSources = await API.sources.getAll();
        const sources = allSources.filter(s => (s.type === 'epg' || s.type === 'xtream') && s.enabled);
        if (sources.length === 0) {
            throw new Error('No EPG sources or Xtream accounts configured');
        }

        const results = await Promise.all(sources.map(async (source) => {
            try {
                const res = await fetch(`/api/proxy/epg/${source.id}/channels`);
                if (!res.ok) throw new Error(`Status ${res.status}`);
                const data = await res.json();
                return { source, channels: data.channels || [] };
            } catch (e) {
                console.warn(`Failed to load EPG channels for source ${source.name}:`, e);
                return { source, channels: [] };
            }
        }));

        this.channels = [];
        this._channelsBySource = new Map();   // epgSourceId -> channels[]
        this._epgChannelSourceById = new Map(); // epgChannelId -> epgSourceId
        results.forEach(({ source, channels }) => {
            this.channels = this.channels.concat(channels);
            this._channelsBySource.set(source.id, channels);
            channels.forEach(ch => this._epgChannelSourceById.set(ch.id, source.id));
        });

        this._buildChannelMaps();
    }

    /**
     * Resolve favorited live channels to their scoped-fetch descriptor - see
     * _scopeForLiveChannels(). Returns null if there's nothing to scope
     * against yet (no live channels, no favorites, or no channelMap), which
     * tells loadEpg() to fall back to a full fetch instead.
     * @returns {Map<number, Set<string>>|null}
     */
    _resolveScopedChannelIdsBySource(liveChannels) {
        if (!liveChannels || liveChannels.length === 0) return null;
        if (!this.favorites || this.favorites.size === 0) return null;
        if (!this.channelMap) return null;
        return this._scopeForLiveChannels(
            liveChannels.filter(ch => this.favorites.has(`${ch.sourceId}:${ch.id}`))
        );
    }

    /**
     * Map a set of live channels (from ChannelList) to the EPG channel ids
     * they resolve to, grouped by which EPG source each belongs to - the
     * descriptor _fetchProgrammesScoped() consumes to pull just those
     * channels' schedules. Shared by the favorites-first load and the
     * per-group on-demand load (selectCategory).
     * @returns {Map<number, Set<string>>|null} null if nothing resolved
     */
    _scopeForLiveChannels(liveChannels) {
        if (!liveChannels || liveChannels.length === 0 || !this.channelMap) return null;
        const bySource = new Map();
        for (const ch of liveChannels) {
            const epgChannel = this.resolveEpgChannel(ch.tvgId, ch.name);
            if (!epgChannel) continue;
            const epgSourceId = this._epgChannelSourceById.get(epgChannel.id);
            if (epgSourceId == null) continue;
            if (!bySource.has(epgSourceId)) bySource.set(epgSourceId, new Set());
            bySource.get(epgSourceId).add(epgChannel.id);
        }
        return bySource.size > 0 ? bySource : null;
    }

    /**
     * Fetch programmes for the scoped channel ids (grouped by source) and
     * merge them into this.programmes. Incremental and idempotent: channel
     * ids whose schedule was already loaded (favorites, or a previously
     * opened group) are skipped, so switching between groups only ever
     * fetches the newly-needed slice, never the whole catalog. Tracks loaded
     * ids in this._loadedProgrammeChannelIds.
     */
    async _fetchProgrammesScoped(bySource) {
        await Promise.all(Array.from(bySource.entries()).map(async ([sourceId, idSet]) => {
            const needed = Array.from(idSet).filter(id => !this._loadedProgrammeChannelIds.has(id));
            if (needed.length === 0) return;
            try {
                const res = await fetch(`/api/proxy/epg/${sourceId}/programmes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ channelIds: needed })
                });
                if (!res.ok) throw new Error(`Status ${res.status}`);
                const data = await res.json();
                if (data.programmes?.length) {
                    this.programmes = this.programmes.concat(data.programmes);
                }
                // Mark requested (not just returned) so channels with genuinely
                // no schedule aren't re-requested every group switch.
                needed.forEach(id => this._loadedProgrammeChannelIds.add(id));
            } catch (e) {
                console.warn(`Failed to load scoped EPG programmes for source ${sourceId}:`, e);
            }
        }));
    }

    /**
     * Build the channelMap/channelMapNormalized lookups (see
     * resolveEpgChannel()) from whatever's currently in this.channels.
     * Shared by the channels-only fetch and the full-catalog fetch below.
     */
    _buildChannelMaps() {
        this.channelMap = new Map();
        // Index by ID, and by exact lowercased name for fallback matching
        this.channels.forEach(ch => {
            this.channelMap.set(ch.id, ch);
            if (ch.name) {
                this.channelMap.set(ch.name.toLowerCase(), ch);
            }
        });
        // Fuzzy index for channels with no epg_channel_id and no exact name
        // match (see normalizeChannelName) - only keep the first channel per
        // normalized key so an ambiguous collision doesn't silently overwrite
        // a good match with an unrelated one.
        this.channelMapNormalized = new Map();
        this.channels.forEach(ch => {
            const norm = normalizeChannelName(ch.name);
            if (norm && !this.channelMapNormalized.has(norm)) {
                this.channelMapNormalized.set(norm, ch);
            }
        });
    }

    /**
     * Fetch the FULL EPG payload (all channels, all programmes) for every
     * source. Used for the background catalog backfill after the fast
     * scoped path, or as a direct fallback when there's nothing to scope
     * against yet (see loadEpg()). Parsed via a Worker (see epgWorker.js) -
     * by far the app's heaviest single payload, so decoding tens of MB of
     * JSON into hundreds of thousands of objects never blocks the UI thread.
     * Also fetches each source's cheap /meta (last-sync timestamp)
     * alongside the real payload, purely so checkForEpgUpdates() has a
     * fresh baseline to compare against later - no extra round trip since
     * it runs in parallel with the real fetch.
     */
    async fetchEpgData() {
        const allSources = await API.sources.getAll();
        const sources = allSources.filter(s => (s.type === 'epg' || s.type === 'xtream') && s.enabled);

        if (sources.length === 0) {
            throw new Error('No EPG sources or Xtream accounts configured');
        }

        const fetchPromises = sources.map(async (source) => {
            try {
                const metaPromise = fetch(`/api/proxy/epg/${source.id}/meta`);
                const data = await this._fetchJsonViaWorker(`/api/proxy/epg/${source.id}`);
                const metaResponse = await metaPromise;
                if (metaResponse.ok) {
                    const meta = await metaResponse.json();
                    this._epgSyncMeta[source.id] = meta.lastSync;
                }
                return { source, data };
            } catch (e) {
                console.warn(`Failed to load EPG for source ${source.name}:`, e);
                return null;
            }
        });

        const results = await Promise.all(fetchPromises);

        this.channels = [];
        this.programmes = [];
        this._channelsBySource = new Map();
        this._epgChannelSourceById = new Map();

        let hasData = false;
        results.forEach(entry => {
            if (entry && entry.data) {
                const { source, data } = entry;
                if (data.channels && data.channels.length > 0) {
                    this.channels = this.channels.concat(data.channels);
                    this._channelsBySource.set(source.id, data.channels);
                    data.channels.forEach(ch => this._epgChannelSourceById.set(ch.id, source.id));
                }
                if (data.programmes && data.programmes.length > 0) {
                    this.programmes = this.programmes.concat(data.programmes);
                }
                if (data.channels || data.programmes) {
                    hasData = true;
                }
            }
        });

        if (!hasData) {
            throw new Error('Failed to load EPG data from any source');
        }

        this._buildChannelMaps();
        // This pulled the whole catalog, so every channel's schedule is now
        // present - mark all-loaded so on-demand group switches don't refetch.
        this._allProgrammesLoaded = true;
        this._loadedProgrammeChannelIds = new Set(this.channels.map(ch => ch.id));
        await this.loadFavorites();
    }

    /**
     * Fetch a URL and parse its JSON body inside a Worker instead of on the
     * main thread - see epgWorker.js for why. Falls back to plain
     * response.json() if Workers aren't available (very old/unusual
     * browsers). A fresh Worker is spawned per call and terminated when
     * done - simpler and fully correct for overlapping concurrent calls
     * (one per EPG source) than trying to share/route messages through a
     * single long-lived worker, and trivial overhead next to the parse
     * work it's about to do.
     */
    async _fetchJsonViaWorker(url) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Status ${response.status}`);

        if (typeof Worker === 'undefined') {
            return response.json();
        }

        const buffer = await response.arrayBuffer();
        return new Promise((resolve, reject) => {
            const worker = new Worker('/js/epgWorker.js?v=1');
            worker.onmessage = (e) => {
                worker.terminate();
                if (e.data.ok) resolve(e.data.data);
                else reject(new Error(e.data.error));
            };
            worker.onerror = (err) => {
                worker.terminate();
                reject(new Error(err.message || 'EPG worker error'));
            };
            worker.postMessage(buffer, [buffer]);
        });
    }

    /**
     * Load favorites - split out from fetchEpgData() so Guide.show() can
     * load this small, fast dataset alongside the channel list instead of
     * only as part of the much slower EPG fetch. Without this, the
     * channels-render-before-EPG optimization (see Guide.js's show())
     * couldn't show real favorited channels on that first, faster render -
     * the default "Favorites" view would render an empty grid (favorites
     * genuinely not loaded yet) until EPG also finished, several seconds
     * to tens of seconds later depending on connection speed. De-duped the
     * same way loadEpg() is, in case both Guide.show() and fetchEpgData()
     * end up calling this around the same time.
     */
    async loadFavorites() {
        if (this._favoritesPromise) return this._favoritesPromise;
        this._favoritesPromise = (async () => {
            const favs = await API.favorites.getAll();
            this.favorites = new Set(favs.map(f => `${f.source_id}:${f.item_id}`));
            this.favoritesLoaded = true;
        })();
        try {
            await this._favoritesPromise;
        } finally {
            this._favoritesPromise = null;
        }
    }

    /**
     * Resolve a source channel (tvg-id + name, e.g. from ChannelList) to its
     * matching EPG channel object, using the same tiered lookup (exact id ->
     * exact lowercased name -> normalized name -> slow fallback scan) that
     * getCurrentProgram() needs. Extracted so other callers (search) can
     * reuse the exact matching behavior instead of re-deriving it.
     * @returns {object|null}
     */
    resolveEpgChannel(tvgId, channelName) {
        if (tvgId && this.channelMap && this.channelMap.has(tvgId)) {
            return this.channelMap.get(tvgId);
        } else if (channelName && this.channelMap && this.channelMap.has(channelName.toLowerCase())) {
            return this.channelMap.get(channelName.toLowerCase());
        } else if (channelName && this.channelMapNormalized) {
            // Most channels have no epg_channel_id from the provider at all, and
            // exact name matching rarely bridges naming convention differences
            // ("CA| CTV RED DEER" vs "CA| CTV Red Deer HD") - normalized name is
            // the last real chance to find this channel's EPG data.
            return this.channelMapNormalized.get(normalizeChannelName(channelName)) || null;
        } else if (!this.channelMap) {
            // Fallback to slow search if maps aren't built yet
            return this.channels.find(epg =>
                (tvgId && epg.id === tvgId) || epg.name === channelName
            ) || null;
        }
        return null;
    }

    /**
     * Get current program for a channel
     * @param {string} tvgId - The EPG channel ID (tvg-id)
     * @param {string} channelName - The channel name (for fallback)
     * @returns {object|null} Program object with title, start, stop
     */
    getCurrentProgram(tvgId, channelName) {
        if (!this.programmes || this.programmes.length === 0) return null;

        const epgChannel = this.resolveEpgChannel(tvgId, channelName);
        if (!epgChannel) return null;

        const now = new Date();
        const nowTime = now.getTime();

        // Filter programs for this channel
        const current = this.programmes.find(p => {
            if (p.channelId !== epgChannel.id) return false;
            const start = new Date(p.start).getTime();
            const stop = new Date(p.stop).getTime();
            return nowTime >= start && nowTime < stop;
        });

        return current ? {
            title: current.title,
            start: current.start,
            stop: current.stop,
            description: current.description
        } : null;
    }

    /**
     * Search programme titles for a term and return a Map of
     * `${sourceId}:${channelId}` -> best matching programme, for every
     * channel in `sourceChannels` whose EPG data contains at least one
     * matching programme. Used by ChannelList's search box to match on
     * "what's showing" rather than just the channel's own name.
     *
     * Currently-airing/upcoming programmes always qualify. A *past*
     * programme only qualifies if that specific channel supports catch-up
     * (`tvArchive`/`tvArchiveDuration`, same Xtream fields the TV Guide's own
     * catch-up button uses - see renderProgrammes()'s archiveCutoffMs), and
     * only as far back as MAX_CATCHUP_LOOKBACK_DAYS or the channel's own
     * (usually shorter) archive window, whichever is smaller - searching
     * back further than the provider actually retains would surface a
     * "match" the channel can't actually play back.
     *
     * Deliberately a plain substring match rather than fuzzy: programme
     * titles need to stay fast to scan across a full EPG window (tens to
     * hundreds of thousands of entries) on every keystroke, and typo
     * tolerance matters far less here than for the smaller, messier channel
     * name list (which uses Fuse.js - see ChannelList.js).
     *
     * @param {string} searchTerm - already-lowercased search term
     * @param {Array} sourceChannels - channel objects with {sourceId, id, tvgId, name, tvArchive, tvArchiveDuration}
     * @returns {Map<string, object>}
     */
    searchProgrammes(searchTerm, sourceChannels) {
        const result = new Map();
        if (!searchTerm || !this.programmes || this.programmes.length === 0) return result;

        const nowTime = Date.now();
        const MAX_CATCHUP_LOOKBACK_MS = 3 * 24 * 60 * 60 * 1000; // hard cap: 3 days

        // Step 1: group every matching programme (any time, not just
        // current/upcoming - the per-channel catch-up window is only known
        // once we get to a specific source channel below) by EPG channel id
        const matchesByEpgChannel = new Map(); // epgChannelId -> matching programmes
        for (const p of this.programmes) {
            if (!p.title || !p.title.toLowerCase().includes(searchTerm)) continue;
            if (!matchesByEpgChannel.has(p.channelId)) matchesByEpgChannel.set(p.channelId, []);
            matchesByEpgChannel.get(p.channelId).push(p);
        }
        if (matchesByEpgChannel.size === 0) return result;

        // Step 2: resolve each source channel and pick its best qualifying match
        for (const ch of sourceChannels) {
            const epgChannel = this.resolveEpgChannel(ch.tvgId, ch.name);
            if (!epgChannel) continue;
            const candidates = matchesByEpgChannel.get(epgChannel.id);
            if (!candidates) continue;

            const catchupWindowMs = (ch.tvArchive && ch.tvArchiveDuration > 0)
                ? Math.min(MAX_CATCHUP_LOOKBACK_MS, ch.tvArchiveDuration * 24 * 60 * 60 * 1000)
                : 0;

            let best = null;
            for (const p of candidates) {
                const stopTime = new Date(p.stop).getTime();
                const isUpcoming = stopTime >= nowTime;
                const isCatchable = !isUpcoming && catchupWindowMs > 0 && (nowTime - stopTime) <= catchupWindowMs;
                if (!isUpcoming && !isCatchable) continue;

                if (!best) { best = p; continue; }
                const bestStop = new Date(best.stop).getTime();
                const bestIsUpcoming = bestStop >= nowTime;
                // Prefer live/upcoming over catch-up; within the same
                // category prefer soonest-upcoming or most-recent-past.
                if (isUpcoming && !bestIsUpcoming) best = p;
                else if (isUpcoming === bestIsUpcoming) {
                    if (isUpcoming ? stopTime < bestStop : stopTime > bestStop) best = p;
                }
            }
            if (best) {
                result.set(`${ch.sourceId}:${ch.id}`, best);
            }
        }
        return result;
    }

    /**
     * Update filtered channels based on search or group
     */
    updateFilteredChannels() {
        const searchTerm = this.searchInput ? this.searchInput.value.toLowerCase().trim() : '';

        // SEARCH MODE: Filter all channels by name
        if (searchTerm) {
            this.filteredChannels = this.allMatchedChannels.filter(ch => {
                const name = (ch.sourceChannel?.name || '').toLowerCase();
                return name.includes(searchTerm);
            });
            return;
        }

        // GROUP MODE (Default)
        if (this.selectedGroup === 'Favorites') {
            this.filteredChannels = this.allMatchedChannels.filter(m =>
                this.favorites.has(`${m.sourceChannel.sourceId}:${m.sourceChannel.id}`)
            );
            return;
        }

        if (!this.selectedGroup || this.selectedGroup === 'All') {
            this.filteredChannels = [...this.allMatchedChannels];
        } else {
            this.filteredChannels = this.allMatchedChannels.filter(m =>
                (m.sourceChannel.groupTitle || 'Uncategorized') === this.selectedGroup
            );
        }
    }

    /**
     * Render the EPG grid
     */
    render() {
        // Get channel list instance
        const channelList = window.app?.channelList;
        if (!channelList) return;

        // Get channels and filter out hidden ones (always enforce hidden in EPG)
        // Note: We only check individual channel visibility, not group visibility
        // A group is implicitly visible if it has any visible children
        const playableChannels = (channelList.channels || []).filter(ch => {
            // Use streamId (raw ID) for hidden check since that's what SourceManager stores
            const rawChannelId = ch.streamId || ch.id;
            const isChannelHidden = channelList.isHidden('channel', ch.sourceId, rawChannelId);
            return !isChannelHidden;
        });



        if (playableChannels.length === 0) {
            this.container.innerHTML = `
                <div class="empty-state">
                    <p>No visible channels available</p>
                    <p class="hint">Check your content settings or add a source</p>
                </div>
            `;
            return;
        }

        // Match ALL playable channels with optional EPG data. Uses the same
        // id -> exact-name -> normalized-name fallback chain as
        // getCurrentProgram() (see there for why the normalized tier matters -
        // most channels have no epg_channel_id from the provider at all). Also
        // avoids an O(channels x epgChannels) linear scan here, which mattered
        // on a catalog this size (up to ~47k channels).
        //
        // This match is cached and only recomputed when the underlying live
        // channel list, channelMap, or hidden-items set actually changed
        // (reference equality - each is only ever reassigned wholesale, never
        // mutated in place, so this is a safe/correct invalidation check).
        // render() is called multiple times in quick succession while a
        // guide is loading (channels first, then again once schedule data
        // lands, again after a background catalog backfill) even though
        // none of those three inputs change between those calls - and this
        // match is real, measurable CPU work at catalog scale (most
        // channels fall through to the regex-based normalizeChannelName()
        // fallback, since most providers don't supply an epg_channel_id),
        // repeating it on every render was multiple seconds of wasted work
        // on a slower CPU for no benefit.
        if (this._matchedChannelsCacheChannels !== channelList.channels ||
            this._matchedChannelsCacheMap !== this.channelMap ||
            this._matchedChannelsCacheHidden !== channelList.hiddenItems) {
            this._matchedChannelsCacheChannels = channelList.channels;
            this._matchedChannelsCacheMap = this.channelMap;
            this._matchedChannelsCacheHidden = channelList.hiddenItems;
            this._matchedChannelsCache = playableChannels.map(sourceChannel => {
                let epgChannel = null;
                if (sourceChannel.tvgId && this.channelMap?.has(sourceChannel.tvgId)) {
                    epgChannel = this.channelMap.get(sourceChannel.tvgId);
                } else if (sourceChannel.name && this.channelMap?.has(sourceChannel.name.toLowerCase())) {
                    epgChannel = this.channelMap.get(sourceChannel.name.toLowerCase());
                } else if (sourceChannel.name && this.channelMapNormalized) {
                    epgChannel = this.channelMapNormalized.get(normalizeChannelName(sourceChannel.name)) || null;
                }
                return { epgChannel, sourceChannel };
            });
        }
        const allChannels = this._matchedChannelsCache;

        // Collect unique groups from ALL playable channels
        const groups = [...new Set(allChannels.map(m => m.sourceChannel.groupTitle || 'Uncategorized'))].sort();

        // Add Favorites at the top if there are any
        const hasFavorites = this.favorites.size > 0;

        // Only rebuild the category rail if groups have changed (performance optimization)
        const groupsKey = groups.join('|') + (hasFavorites ? '|FAV' : '');
        if (this._lastGroupsKey !== groupsKey) {
            this._lastGroupsKey = groupsKey;

            const chipList = [];
            if (hasFavorites) chipList.push({ value: 'Favorites', label: 'Favorites' });
            chipList.push({ value: '', label: 'All Groups' });
            groups.forEach(g => chipList.push({ value: g, label: g }));

            this.categoryRail.setItems(chipList, this.selectedGroup);
        } else {
            this.categoryRail.setValue(this.selectedGroup);
        }

        // Handle case where we defaulted to Favorites but user has none -
        // gated on favoritesLoaded so the channels-render-before-EPG pass
        // (favorites aren't fetched yet at that point - see fetchEpgData())
        // doesn't mistake "not loaded yet" for "genuinely no favorites" and
        // permanently fall back to All Groups before we actually know.
        if (this.selectedGroup === 'Favorites' && this.favoritesLoaded && !hasFavorites) {
            this.selectedGroup = ''; // Fallback to 'All Groups'
            this.categoryRail.setValue('');
        }

        // Store all channels (matched with EPG data) for filtering
        this.allMatchedChannels = allChannels;
        this.updateFilteredChannels();

        // Calculate time range and store for batch rendering. The grid always
        // spans [anchor - pastHours, anchor + 24h], where anchor is "now"
        // shifted by timeOffset (prev/next paging) - so the last `pastHours`
        // of already-aired programmes are always in the rendered range and
        // reachable by scrolling left, without needing a page/re-fetch.
        const anchor = new Date();
        anchor.setHours(anchor.getHours() + this.timeOffset);

        this.startTime = new Date(anchor);
        this.startTime.setHours(this.startTime.getHours() - this.pastHours);
        this.startTime.setMinutes(0, 0, 0);

        this.endTime = new Date(this.startTime);
        this.endTime.setHours(this.endTime.getHours() + this.pastHours + 24);

        // Update date display
        this.updateDateDisplay(anchor);

        // Generate time slots
        const timeSlots = this.generateTimeSlots(this.startTime, this.endTime);

        // Calculate total height for virtual scrolling
        const totalHeight = this.filteredChannels.length * this.rowHeight;

        // Total width of the scrollable timeline. Rows below are `position:
        // absolute; left:0; right:0` inside .epg-spacer, which makes .epg-spacer
        // their sticky containing block - without an explicit width here it
        // defaults to the visible viewport width, so .epg-channel-info's
        // `position: sticky` can only stick within that narrow box and gets
        // dragged off-screen the moment scrollLeft exceeds it (which happens
        // immediately, since the grid auto-scrolls to "now" ~pastHours in).
        const totalWidth = (this.endTime - this.startTime) / 60000 * this.pixelsPerMinute;

        // Build HTML structure - header is INSIDE scroll container for natural sync
        this.container.innerHTML = `
      <div class="epg-container" style="position: relative; height: 100%;">
        <div class="epg-scroll-container" style="overflow: auto; height: 100%;">
          <div class="epg-time-header">
            <div class="epg-header-corner"></div>
            <div class="epg-time-slots">
              ${timeSlots.map(slot => `
                <div class="epg-time-slot" style="width: ${30 * this.pixelsPerMinute}px;">
                  ${slot.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              `).join('')}
            </div>
          </div>
          <div class="epg-spacer" style="height: ${totalHeight}px; width: ${totalWidth}px; position: relative;">
            <div class="epg-channel-rows" role="grid" aria-label="TV schedule" style="position: absolute; top: 0; left: 0; right: 0;"></div>
          </div>
        </div>
      </div>
    `;

        // Get references for virtual scrolling
        this.scrollContainer = this.container.querySelector('.epg-scroll-container');
        this.epgSpacer = this.container.querySelector('.epg-spacer');
        this.epgContainer = this.container.querySelector('.epg-channel-rows');

        // Clear visible rows cache
        this.visibleRows.clear();
        this._lastVisibleStart = -1;
        this._lastVisibleEnd = -1;

        // Remove old scroll handler if exists
        if (this._scrollHandler) {
            this.scrollContainer?.removeEventListener('scroll', this._scrollHandler);
        }
        if (this._hScrollHandler) {
            this.scrollContainer?.removeEventListener('scroll', this._hScrollHandler);
        }

        // Get reference to time slots container for horizontal scroll sync
        this.timeSlotsContainer = this.container.querySelector('.epg-time-slots');

        // Set up scroll handler for virtual scrolling (debounced for performance)
        this._scrollHandler = this.debounce(() => this.updateVisibleRows(), 16); // ~60fps
        this.scrollContainer.addEventListener('scroll', this._scrollHandler);

        // No need for horizontal scroll sync anymore - header is inside scroll container

        // Initial render of visible rows
        this.updateVisibleRows();

        // Sync header corner width with actual sidebar width (handles CSS overrides)
        this.syncHeaderCornerWidth();

        // Position the horizontal scroll at "now" (+timeOffset) rather than
        // the very start of the widened (pastHours back) range, so the grid
        // still opens showing live/upcoming programming by default - the
        // extra days of history sit off-screen to the left, reachable by
        // scrolling/dragging back.
        this.scrollToNow();

        // Add now indicator and set up periodic refresh
        this.updateNowIndicator();
        // Clear any existing interval
        if (this._nowIndicatorInterval) {
            clearInterval(this._nowIndicatorInterval);
        }
        // Update indicator every 60 seconds
        this._nowIndicatorInterval = setInterval(() => this.updateNowIndicator(), 60000);
    }

    /**
     * Sync header corner width with actual sidebar width
     * This handles cases where CSS variables are overridden on mobile
     */
    syncHeaderCornerWidth() {
        const sidebar = this.container.querySelector('.epg-channel-info');
        const headerCorner = this.container.querySelector('.epg-header-corner');
        if (sidebar && headerCorner) {
            const actualWidth = sidebar.offsetWidth;
            headerCorner.style.width = `${actualWidth}px`;
        }
    }

    /**
     * Update visible rows based on scroll position (Virtual Scrolling)
     */
    updateVisibleRows() {
        if (!this.scrollContainer || !this.epgContainer) return;

        const scrollTop = this.scrollContainer.scrollTop;
        const viewportHeight = this.scrollContainer.clientHeight;

        // Calculate visible range
        const startIndex = Math.max(0, Math.floor(scrollTop / this.rowHeight) - this.bufferRows);
        const endIndex = Math.min(
            this.filteredChannels.length - 1,
            Math.ceil((scrollTop + viewportHeight) / this.rowHeight) + this.bufferRows
        );

        // Skip if nothing changed
        if (startIndex === this._lastVisibleStart && endIndex === this._lastVisibleEnd) {
            return;
        }

        this._lastVisibleStart = startIndex;
        this._lastVisibleEnd = endIndex;

        // Determine which rows to add and remove
        const newVisibleSet = new Set();
        for (let i = startIndex; i <= endIndex; i++) {
            newVisibleSet.add(i);
        }

        // Remove rows that are no longer visible
        for (const [index, row] of this.visibleRows) {
            if (!newVisibleSet.has(index)) {
                row.remove();
                this.visibleRows.delete(index);
            }
        }

        // Add new visible rows
        for (let i = startIndex; i <= endIndex; i++) {
            if (!this.visibleRows.has(i) && i < this.filteredChannels.length) {
                const row = this.createChannelRow(i);
                this.visibleRows.set(i, row);
                this.epgContainer.appendChild(row);
            }
        }
    }

    /**
     * Lightweight refilter for the search input - unlike render(), this does
     * NOT rebuild the .epg-container DOM shell (which would create a new
     * .epg-scroll-container and reset both scroll axes to 0) or call
     * scrollToNow() (which would snap the horizontal scroll back to "now").
     * Concrete bug this fixes: typing a search term, scrolling to look at a
     * match, then a stray keystroke's debounced re-filter used to yank the
     * view back to the top-left "now" position mid-read. Only re-runs the
     * cheap filter + row-window recompute; the time header/grid shell stay
     * exactly as they are.
     */
    refilterInPlace() {
        if (!this.scrollContainer || !this.epgContainer || !this.epgSpacer) {
            // Grid shell not built yet (e.g. search typed before the first
            // render() completes) - fall back to a full render.
            this.render();
            return;
        }

        this.updateFilteredChannels();

        // Row count changed - update scrollable height and drop the stale
        // visible-row DOM (their content no longer matches the new filtered
        // list at those indices) so updateVisibleRows() rebuilds them fresh
        // instead of short-circuiting on "nothing changed" (same scrollTop
        // range as before).
        this.epgSpacer.style.height = `${this.filteredChannels.length * this.rowHeight}px`;
        for (const row of this.visibleRows.values()) row.remove();
        this.visibleRows.clear();
        this._lastVisibleStart = -1;
        this._lastVisibleEnd = -1;

        this.updateVisibleRows();
    }

    /**
     * Create a channel row element for virtual scrolling
     */
    createChannelRow(index) {
        const { epgChannel, sourceChannel } = this.filteredChannels[index];
        const isFavorite = this.favorites.has(`${sourceChannel.sourceId}:${sourceChannel.id}`);

        // Get programs if EPG data exists
        let channelProgrammes = [];
        if (epgChannel) {
            channelProgrammes = this.programmes
                .filter(p => p.channelId === epgChannel.id)
                .filter(p => {
                    const start = new Date(p.start);
                    const stop = new Date(p.stop);
                    return start < this.endTime && stop > this.startTime;
                })
                .sort((a, b) => new Date(a.start) - new Date(b.start));
        }

        // Fallback values if EPG channel is missing
        const logo = this.getProxiedImageUrl(sourceChannel.tvgLogo || (epgChannel && epgChannel.icon));
        const name = sourceChannel.name || (epgChannel && epgChannel.name);

        const row = document.createElement('div');
        row.className = 'epg-channel-row';
        row.setAttribute('role', 'row');
        row.dataset.channelId = sourceChannel.id;
        row.dataset.sourceId = sourceChannel.sourceId;
        row.dataset.index = index;
        // Position absolutely for virtual scrolling
        row.style.position = 'absolute';
        row.style.top = `${index * this.rowHeight}px`;
        row.style.left = '0';
        row.style.right = '0';
        row.style.height = `${this.rowHeight}px`;

        row.innerHTML = `
          <div class="epg-channel-info">
            <button class="favorite-btn ${isFavorite ? 'active' : ''}" title="${isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}" aria-label="${isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}">
              ${isFavorite ? Icons.favorite : Icons.favoriteOutline}
            </button>
            <img class="epg-channel-logo" src="${logo}" loading="lazy"
                 alt="" onerror="this.onerror=null;this.src='/img/placeholder.png'">
            <span class="epg-channel-name">${escapeHtml(name)}</span>
            <div class="resize-handle"></div>
          </div>
          <div class="epg-programs">
            ${this.renderProgrammes(channelProgrammes, this.startTime, this.endTime, sourceChannel)}
          </div>
        `;

        this.attachRowListeners(row);
        return row;
    }

    /**
     * Attach event listeners to an EPG row
     */
    attachRowListeners(row) {
        // Program click handlers
        row.querySelectorAll('.epg-program').forEach(prog => {
            prog.addEventListener('click', () => this.showProgramDetails(prog.dataset));
            // Cells are real gridcells now (role="gridcell" tabindex="0" in
            // renderProgrammes()) - without this, a keyboard/switch-access
            // user could tab onto a programme but never open it or start
            // catch-up playback, the grid's entire reason to exist.
            prog.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                this.showProgramDetails(prog.dataset);
            });
        });

        const info = row.querySelector('.epg-channel-info');
        if (info) {
            // Name/Logo click plays channel
            info.querySelector('.epg-channel-name')?.addEventListener('click', (e) => {
                e.stopPropagation();
                this.playChannel(info.querySelector('.epg-channel-name').textContent);
            });
            info.querySelector('.epg-channel-logo')?.addEventListener('click', (e) => {
                e.stopPropagation();
                this.playChannel(info.querySelector('.epg-channel-name').textContent);
            });

            // Double-click a channel to jump straight to fullscreen
            // (TiviMate-style) - single click already played it above.
            info.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                this.playChannel(info.querySelector('.epg-channel-name').textContent);
                window.app?.player?.enterFullscreen();
            });

            // Favorite click
            const favBtn = info.querySelector('.favorite-btn');
            if (favBtn) {
                favBtn.addEventListener('click', async (e) => {
                    e.stopPropagation();
                    const sourceId = parseInt(row.dataset.sourceId);
                    const channelId = row.dataset.channelId;
                    await this.toggleFavorite(sourceId, channelId);
                });
            }
        }
    }

    /**
     * Paint every visible favorite-btn for a channel to match isFavorite.
     * Shared by toggleFavorite()'s optimistic update + its error-path revert,
     * and by syncFavorite() - all three used to duplicate this same
     * query-and-toggle block independently.
     */
    _paintFavoriteButtons(sourceId, channelId, isFavorite) {
        const rows = this.container.querySelectorAll(`.epg-channel-row[data-channel-id="${channelId}"][data-source-id="${sourceId}"]`);
        rows.forEach(row => {
            const btn = row.querySelector('.favorite-btn');
            if (btn) {
                btn.classList.toggle('active', isFavorite);
                btn.innerHTML = isFavorite ? Icons.favorite : Icons.favoriteOutline;
                btn.title = isFavorite ? 'Remove from Favorites' : 'Add to Favorites';
                btn.setAttribute('aria-label', btn.title);
            }
        });
        return rows;
    }

    /**
     * Toggle favorite
     */
    async toggleFavorite(sourceId, channelId) {
        const key = `${sourceId}:${channelId}`;
        if (this._toggleInFlight.has(key)) return;
        this._toggleInFlight.add(key);
        const wasFavorite = this.favorites.has(key);
        const isNowFavorite = !wasFavorite;

        // 1. Optimistic Update (EPG)
        if (isNowFavorite) {
            this.favorites.add(key);
        } else {
            this.favorites.delete(key);
        }
        this._paintFavoriteButtons(sourceId, channelId, isNowFavorite);

        // 2. Sync Channel List State (Optimistic)
        if (window.app?.channelList) {
            window.app.channelList.syncFavorite(sourceId, channelId, isNowFavorite);
        }

        try {
            // 3. API Call
            if (wasFavorite) {
                await API.favorites.remove(sourceId, channelId, 'channel');
            } else {
                await API.favorites.add(sourceId, channelId, 'channel');
            }

            // 4. Re-render if viewing Favorites group (so new favorites appear immediately)
            if (this.selectedGroup === 'Favorites') {
                this.render();
            }
        } catch (err) {
            console.error('Error toggling favorite in EPG:', err);

            // Revert EPG
            if (wasFavorite) {
                this.favorites.add(key);
            } else {
                this.favorites.delete(key);
            }
            this._paintFavoriteButtons(sourceId, channelId, wasFavorite);

            // Revert Channel List
            if (window.app?.channelList) {
                window.app.channelList.syncFavorite(sourceId, channelId, wasFavorite);
            }
        } finally {
            this._toggleInFlight.delete(key);
        }
    }

    /**
     * Sync favorite status from external source (e.g. ChannelList) without API call
     */
    syncFavorite(sourceId, channelId, isFavorite) {
        // Ensure consistent string format for key
        const key = `${String(sourceId)}:${String(channelId)}`;
        const currentlyFav = this.favorites.has(key);

        if (currentlyFav === isFavorite) return; // No change needed

        // Update State
        if (isFavorite) {
            this.favorites.add(key);
        } else {
            this.favorites.delete(key);
        }
        this._paintFavoriteButtons(sourceId, channelId, isFavorite);

        // Note: We don't call render() here - the favorites Set is updated
        // and will be used when the user navigates to Guide or switches groups
    }

    /**
     * Generate time slots
     */
    generateTimeSlots(start, end) {
        const slots = [];
        const current = new Date(start);

        while (current < end) {
            slots.push(new Date(current));
            current.setMinutes(current.getMinutes() + 30);
        }

        return slots;
    }

    /**
     * Render programmes for a channel
     */
    renderProgrammes(programmes, startTime, endTime, sourceChannel) {
        if (programmes.length === 0) {
            const width = (endTime - startTime) / 60000 * this.pixelsPerMinute;
            return `<div class="epg-program" style="width: ${width}px;"><span class="epg-program-title">No data</span></div>`;
        }

        const now = new Date();
        const nowMs = now.getTime();
        // Xtream catch-up: only offered for a past program, on a channel that
        // supports it, and only as far back as the provider's own archive
        // window (tv_archive_duration, in days) actually covers.
        const archiveOk = sourceChannel?.tvArchive && sourceChannel.tvArchiveDuration > 0;
        const archiveCutoffMs = archiveOk ? nowMs - (sourceChannel.tvArchiveDuration * 24 * 60 * 60 * 1000) : null;

        let html = '';
        let currentPos = startTime.getTime();

        for (const prog of programmes) {
            const progStartMs = new Date(prog.start).getTime();
            const progStopMs = new Date(prog.stop).getTime();
            const progStart = Math.max(progStartMs, startTime.getTime());
            const progEnd = Math.min(progStopMs, endTime.getTime());

            // Fill gap if needed
            if (progStart > currentPos) {
                const gapWidth = (progStart - currentPos) / 60000 * this.pixelsPerMinute;
                html += `<div class="epg-program" style="width: ${gapWidth}px;"></div>`;
            }

            const width = (progEnd - progStart) / 60000 * this.pixelsPerMinute;
            const isCurrent = progStartMs <= nowMs && progStopMs > nowMs;
            const isPast = progStopMs <= nowMs;
            const canCatchup = isPast && archiveOk && progStartMs >= archiveCutoffMs;

            html += `
        <div class="epg-program ${isCurrent ? 'current' : ''} ${canCatchup ? 'has-catchup' : ''}"
             style="width: ${width}px;"
             role="gridcell" tabindex="0"
             data-title="${escapeHtml(prog.title || '')}"
             data-channel-id="${prog.channelId || ''}"
             data-start="${prog.start}"
             data-stop="${prog.stop}"
             data-can-catchup="${canCatchup ? '1' : ''}"
             data-source-id="${sourceChannel?.sourceId ?? ''}"
             data-stream-id="${sourceChannel?.streamId ?? ''}">
          <div class="epg-program-title">${escapeHtml(prog.title || 'Unknown')}${canCatchup ? `<span class="catchup-badge" title="Watch again">${Icons.catchup}</span>` : ''}</div>
          <div class="epg-program-time">
            ${new Date(prog.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      `;

            currentPos = progEnd;
        }

        return html;
    }

    /**
     * Update date display
     */
    updateDateDisplay(date) {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) {
            this.dateDisplay.textContent = 'Today';
        } else if (date.toDateString() === tomorrow.toDateString()) {
            this.dateDisplay.textContent = 'Tomorrow';
        } else {
            this.dateDisplay.textContent = date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
        }
    }

    /**
     * Debounce utility
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    updateNowIndicator() {
        const now = new Date();
        // Place indicator inside the spacer so it scrolls with content
        const spacer = this.container.querySelector('.epg-spacer');
        if (!spacer) return;

        // Remove existing indicator
        const existing = spacer.querySelector('.epg-now-line');
        if (existing) existing.remove();

        // Calculate position relative to EPG start time
        const minutesFromStart = (now - this.startTime) / 60000;
        const totalMinutes = (this.endTime - this.startTime) / 60000;
        if (minutesFromStart < 0 || minutesFromStart > totalMinutes) return; // Not in visible range

        // Get sidebar width to offset the indicator
        const sidebar = this.container.querySelector('.epg-channel-info');
        const sidebarWidth = sidebar ? sidebar.offsetWidth : 150;

        // Position is sidebar + time offset
        const leftPos = sidebarWidth + (minutesFromStart * this.pixelsPerMinute);

        const indicator = document.createElement('div');
        indicator.className = 'epg-now-line';
        indicator.style.left = `${leftPos}px`;
        spacer.appendChild(indicator);
    }

    /**
     * Scroll the grid so "now" (+timeOffset) sits just inside the left edge
     * of the viewport - see the call site in render() for why. Falls back to
     * the very start of the range if the anchor point isn't in it (shouldn't
     * normally happen since the range is always built around it).
     */
    scrollToNow() {
        if (!this.scrollContainer) return;
        const anchorMinutesFromStart = this.pastHours * 60;
        const px = anchorMinutesFromStart * this.pixelsPerMinute;
        this.scrollContainer.scrollLeft = Math.max(0, px - 40);
    }

    /**
     * Show program details modal
     */
    showProgramDetails(data) {
        const modal = document.getElementById('modal');
        const title = document.getElementById('modal-title');
        const body = document.getElementById('modal-body');
        const footer = document.getElementById('modal-footer');

        title.textContent = data.title || 'Program Details';

        const start = new Date(parseEpochMs(data.start));
        const stop = new Date(parseEpochMs(data.stop));

        body.innerHTML = `
      <p><strong>Time:</strong> ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${stop.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
      <p><strong>Description:</strong></p>
      <p id="epg-program-description">Loading…</p>
    `;

        // Descriptions aren't in the bulk EPG payload (kept small on purpose) -
        // fetch just this programme's on demand, keyed by channel id + start.
        const descEl = document.getElementById('epg-program-description');
        const sourceId = data.sourceId;
        const channelId = data.channelId;
        const startMs = parseEpochMs(data.start);
        if (descEl && sourceId && channelId && Number.isFinite(startMs)) {
            API.proxy.epg.getDescription(sourceId, channelId, startMs)
                .then(res => { descEl.textContent = (res && res.description) || 'No description available'; })
                .catch(() => { descEl.textContent = 'No description available'; });
        } else if (descEl) {
            descEl.textContent = 'No description available';
        }

        const canCatchup = data.canCatchup === '1';
        footer.innerHTML = `
      ${canCatchup ? `<button class="btn btn-primary" id="modal-watch-catchup">${Icons.catchup} Watch</button>` : ''}
      <button class="btn btn-secondary" id="modal-close">Close</button>
    `;

        modal.classList.add('active');
        document.getElementById('modal-close').onclick = () => modal.classList.remove('active');
        modal.querySelector('.modal-close').onclick = () => modal.classList.remove('active');

        if (canCatchup) {
            document.getElementById('modal-watch-catchup').onclick = () => {
                modal.classList.remove('active');
                this.playCatchupProgram(data);
            };
        }
    }

    /**
     * Play a past program via Xtream catch-up/timeshift.
     */
    async playCatchupProgram(data) {
        const sourceId = parseInt(data.sourceId, 10);
        const streamId = data.streamId;
        const startMs = parseEpochMs(data.start);
        const stopMs = parseEpochMs(data.stop);
        const durationMinutes = Math.max(1, Math.round((stopMs - startMs) / 60000));

        if (!sourceId || !streamId) {
            console.error('[EpgGuide] Cannot play catch-up: missing source/stream id', data);
            return;
        }

        const mySelection = ++this.catchupSelectionGeneration;
        try {
            const { url } = await API.proxy.xtream.getTimeshiftUrl(sourceId, streamId, startMs, durationMinutes);
            if (mySelection !== this.catchupSelectionGeneration) return; // a newer selection has already taken over

            // Reuse the real channel object if we have it (for logo/name/tvgId),
            // falling back to a minimal synthetic one built from what the EPG
            // grid already knows.
            const channel = window.app?.channelList?.channels.find(c =>
                String(c.sourceId) === String(sourceId) && String(c.streamId) === String(streamId)
            ) || { id: `xtream_${sourceId}_${streamId}`, streamId, sourceId, sourceType: 'xtream', name: data.title };

            if (window.app?.player) {
                await window.app.player.play(channel, url, {
                    catchup: { title: data.title, start: data.start, stop: data.stop }
                });
            }
            if (mySelection !== this.catchupSelectionGeneration) return; // superseded while play() was starting

            // Update the "Now Playing" label - the video is already visible
            // in the Guide's preview slot, no navigation needed (Live TV
            // playback happens entirely within this page now).
            window.app?.updateGuideOverlayNowPlaying(channel.name || data.title);
        } catch (err) {
            console.error('[EpgGuide] Failed to start catch-up playback:', err);
        }
    }

    /**
     * Play channel from EPG
     */
    async playChannel(channelName) {
        // Find channel in channel list and play
        if (window.app?.channelList) {
            let channel = window.app.channelList.channels.find(c =>
                c.name === channelName || c.tvgName === channelName
            );
            if (!channel) {
                // Same reasoning as EpgGuide's own EPG-channel matching: exact
                // name matching frequently fails to bridge naming convention
                // differences between the live channel list and the EPG.
                const normTarget = normalizeChannelName(channelName);
                channel = window.app.channelList.channels.find(c =>
                    normalizeChannelName(c.name) === normTarget || normalizeChannelName(c.tvgName) === normTarget
                );
            }
            if (channel) {
                // selectChannel() itself updates the "Now Playing" label.
                await window.app.channelList.selectChannel({ channelId: channel.id });
            }
        }
    }
}

// Export
window.EpgGuide = EpgGuide;
